import React, { useEffect, useRef, useState } from "react";
import { project, CITIES, ROUTES } from "./geo";

/* Static 2D version of the journey map: one canvas, drawn once. Used for
   reduced motion, data saver, low-end devices or when WebGL is unavailable,
   and as the poster while the 3D scene loads. */
const LABELLED = ["istanbul", "guangzhou", "aleppo", "damascus"];

export default function RouteMapFallback({ cities, className = "" }) {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const [labels, setLabels] = useState({});

  useEffect(() => {
    let MAP_DOTS = null;
    let MAP_BOUNDS = null;
    let cancelled = false;
    const draw = () => {
      if (!MAP_DOTS) return;
      const el = canvas.current;
      const box = wrap.current;
      if (!el || !box) return;
      const w = box.clientWidth;
      const h = box.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      el.width = w * dpr;
      el.height = h * dpr;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      const ctx = el.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Frame the Türkiye → China band with a little padding.
      const [x0, y1] = project(MAP_BOUNDS.lon0 + 6, MAP_BOUNDS.lat1);
      const [x1, y0] = project(MAP_BOUNDS.lon1, MAP_BOUNDS.lat0 + 2);
      const scale = Math.min(w / (x1 - x0), h / (y1 - y0));
      const ox = (w - (x1 - x0) * scale) / 2;
      const oy = (h - (y1 - y0) * scale) / 2;
      const toPx = (lon, lat) => {
        const [x, y] = project(lon, lat);
        return [ox + (x - x0) * scale, oy + (y1 - y) * scale];
      };

      const r = Math.max(0.6, scale * 0.17);
      for (let i = 0; i < MAP_DOTS.length; i += 3) {
        const tag = MAP_DOTS[i + 2];
        const [px, py] = toPx(MAP_DOTS[i] / 10, MAP_DOTS[i + 1] / 10);
        ctx.fillStyle = tag === 3 ? "#d6f25a" : tag ? "#5d8a52" : "#2f4a33";
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
      }

      const line = (pts, color, width, dash) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.setLineDash(dash || []);
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.beginPath();
        pts.forEach(([lon, lat], idx) => {
          const [px, py] = toPx(lon, lat);
          if (idx === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.stroke();
      };
      line(ROUTES.sea, "#8fc7b8", 2, [6, 5]);
      line(ROUTES.road, "#d6f25a", 2.5);
      // Air: a gentle arc
      const [ax, ay] = toPx(...ROUTES.air[0]);
      const [bx, by] = toPx(...ROUTES.air[1]);
      ctx.strokeStyle = "#f2f5ef";
      ctx.setLineDash([2, 6]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.quadraticCurveTo((ax + bx) / 2, Math.min(ay, by) - h * 0.28, bx, by);
      ctx.stroke();
      ctx.setLineDash([]);

      const next = {};
      Object.entries(CITIES).forEach(([key, [lon, lat]]) => {
        const [px, py] = toPx(lon, lat);
        ctx.fillStyle = "#d6f25a";
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fill();
        if (LABELLED.includes(key)) next[key] = { x: px, y: py };
      });
      setLabels(next);
    };

    // The dot grid is ~23 kB gzipped: load it on demand, shared with the 3D scene's chunk.
    import(/* webpackChunkName: "map-dots" */ "./mapDots").then((m) => {
      if (cancelled) return;
      MAP_DOTS = m.MAP_DOTS;
      MAP_BOUNDS = m.MAP_BOUNDS;
      draw();
    });
    const ro = new ResizeObserver(draw);
    ro.observe(wrap.current);
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={wrap} className={`relative ${className}`} aria-hidden>
      <canvas ref={canvas} className="absolute inset-0" />
      {Object.entries(labels).map(([key, p]) => (
        <span
          key={key}
          className="absolute -translate-x-1/2 -translate-y-[160%] whitespace-nowrap rounded-md bg-[oklch(0.17_0.028_140/0.85)] px-2 py-0.5 text-xs font-semibold text-[var(--on-dark)]"
          style={{ left: p.x, top: p.y }}
        >
          {cities?.[key] || key}
        </span>
      ))}
    </div>
  );
}
